import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MdPlayArrow, MdPause, MdSkipPrevious, MdSkipNext,
  MdFastRewind, MdFastForward,
} from 'react-icons/md';
import { useApp } from '../contexts/AppContext';
import { fetchScenario } from '../services/backendAPI';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function toUTC(s) { const [y,m,d] = String(s||'').split('-').map(Number); return Date.UTC(y,(m||1)-1,d||1); }
function addDays(s,n) { return new Date(toUTC(s)+n*86400000).toISOString().slice(0,10); }
function daysBetween(a,b) { return Math.round((toUTC(b)-toUTC(a))/86400000); }
function shortLabel(s) {
  const [y,m,d] = String(s||'').split('-').map(Number);
  if (!y||!m||!d) return '—';
  return `${MONTHS[m-1]} ${d}, ${y}`;
}

const SPEEDS = { days:[1,4,16], txns:[1,5,20] };
const TICK_MS = 900;
const KIND_ICON = { month_rollover:'📅', limit_crossed:'📊', shortfall:'⚠️', anomaly:'🔎', fraud:'🚨' };
const ACCENT = '#2FBF00';

const mono = { fontFamily:'monospace' };
const c = { text:'#cfcfcf', muted:'#777', dim:'#444', border:'#2a2a2a' };

const ctrlBtn = { width:30, height:30, borderRadius:15, border:`1px solid ${c.border}`, background:'#1e1e1e', color:c.text, display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 };
const playBtnStyle = (playing) => ({ width:36, height:36, borderRadius:18, border:'none', background: playing ? '#c0392b' : ACCENT, color:'#000', display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 });
const segStyle = (active) => ({ ...mono, fontSize:9, padding:'3px 9px', border:'none', background: active ? `${ACCENT}28` : 'transparent', color: active ? ACCENT : c.muted, cursor:'pointer', letterSpacing:0.5 });
const pillStyle = (active) => ({ ...mono, fontSize:9, padding:'2px 8px', borderRadius:8, border:`1px solid ${active ? ACCENT : c.border}`, background: active ? `${ACCENT}28` : 'transparent', color: active ? ACCENT : c.muted, cursor:'pointer', letterSpacing:0.4 });

export default function ScenarioConsoleTab() {
  const { sessionId, simDate, setSimDate } = useApp();

  const [timeline, setTimeline]     = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [playing, setPlaying]       = useState(false);
  const [mode, setMode]             = useState('txns');
  const [speedIdx, setSpeedIdx]     = useState(0);

  useEffect(() => {
    if (!sessionId) { setTimeline(null); setMilestones([]); setSimDate(null); setPlaying(false); return; }
    let cancelled = false;
    setLoading(true); setPlaying(false);
    fetchScenario(sessionId).then(r => {
      if (cancelled) return;
      setLoading(false);
      if (r?.ready && r.timeline?.dates?.length) {
        setTimeline(r.timeline);
        setMilestones(r.milestones || []);
        setSimDate(d => d ?? addDays(r.timeline.min, -1));
      } else {
        setTimeline(null); setMilestones([]); setSimDate(null);
      }
    });
    return () => { cancelled = true; };
  }, [sessionId, setSimDate]);

  const minDate   = timeline?.min || null;
  const maxDate   = timeline?.max || null;
  const startDate = minDate ? addDays(minDate, -1) : null;
  const spanDays  = startDate && maxDate ? daysBetween(startDate, maxDate) : 0;

  const expanded = useMemo(() => {
    if (!timeline) return [];
    const out = [];
    for (const d of timeline.dates) { const n = timeline.countsByDate[d]||0; for (let i=0;i<n;i++) out.push(d); }
    return out;
  }, [timeline]);

  const consumed = useMemo(() => {
    if (!simDate) return 0;
    let n = 0; for (const d of expanded) { if (d <= simDate) n++; else break; } return n;
  }, [expanded, simDate]);

  const step = useCallback((prev, dir) => {
    if (!timeline) return prev;
    const speed = SPEEDS[mode][speedIdx];
    if (mode === 'days') {
      const next = addDays(prev, dir * speed);
      return next <= startDate ? startDate : next >= maxDate ? maxDate : next;
    }
    let cnt = 0; for (const d of expanded) { if (d <= prev) cnt++; else break; }
    const target = Math.max(0, Math.min(expanded.length, cnt + dir * speed));
    return target <= 0 ? startDate : expanded[target - 1];
  }, [timeline, mode, speedIdx, expanded, startDate, maxDate]);

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      setSimDate(prev => {
        const next = step(prev, +1);
        if (next === prev || next >= maxDate) setPlaying(false);
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(id);
  }, [playing, step, maxDate, setSimDate]);

  const scrubValue = simDate && startDate ? Math.max(0, Math.min(spanDays, daysBetween(startDate, simDate))) : 0;
  const pctDone    = spanDays > 0 ? Math.round((scrubValue / spanDays) * 100) : 0;

  const reset   = () => { setPlaying(false); setSimDate(startDate); };
  const jumpEnd = () => { setPlaying(false); setSimDate(maxDate); };
  const onScrub = (v) => { setPlaying(false); setSimDate(addDays(startDate, Number(v))); };

  const emptyStyle = { flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:c.muted, ...mono, fontSize:10 };
  if (!sessionId) return <div style={emptyStyle}>load a profile to run a scenario</div>;
  if (loading)    return <div style={emptyStyle}>building timeline…</div>;
  if (!timeline)  return <div style={emptyStyle}>no transactions to replay for this profile</div>;

  return (
    <div style={{flex:1, overflowY:'auto', minHeight:0, padding:'12px 14px', display:'flex', flexDirection:'column', gap:12}}>

      {/* date + counter */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline'}}>
        <span style={{...mono, fontSize:14, fontWeight:700, color:c.text, letterSpacing:0.3}}>
          {shortLabel(simDate)}
        </span>
        <span style={{...mono, fontSize:9, color:c.muted, letterSpacing:0.4}}>
          {consumed} / {timeline.total} txns · {pctDone}%
        </span>
      </div>

      {/* scrub bar */}
      <div>
        <input
          type="range" min={0} max={spanDays} value={scrubValue}
          onMouseDown={() => setPlaying(false)}
          onTouchStart={() => setPlaying(false)}
          onInput={(e) => onScrub(e.target.value)}
          onChange={(e) => onScrub(e.target.value)}
          style={{width:'100%', accentColor:ACCENT, cursor:'pointer', margin:'2px 0'}}
        />
        <div style={{display:'flex', justifyContent:'space-between', ...mono, fontSize:9, color:c.dim}}>
          <span>{shortLabel(minDate)}</span>
          <span>{shortLabel(maxDate)}</span>
        </div>
      </div>

      {/* transport */}
      <div style={{display:'flex', gap:8, alignItems:'center', justifyContent:'center'}}>
        <button style={ctrlBtn} onClick={reset}                                                title="Go to start"><MdSkipPrevious size={16} /></button>
        <button style={ctrlBtn} onClick={() => { setPlaying(false); setSimDate(p => step(p,-1)); }} title="Step back"><MdFastRewind size={16} /></button>
        <button style={playBtnStyle(playing)} onClick={() => setPlaying(p => !p)}              title={playing ? 'Pause' : 'Play'}>
          {playing ? <MdPause size={20} /> : <MdPlayArrow size={20} />}
        </button>
        <button style={ctrlBtn} onClick={() => { setPlaying(false); setSimDate(p => step(p,+1)); }} title="Step forward"><MdFastForward size={16} /></button>
        <button style={ctrlBtn} onClick={jumpEnd}                                              title="Go to end"><MdSkipNext size={16} /></button>
      </div>

      {/* mode + speed */}
      <div style={{display:'flex', gap:6, alignItems:'center', justifyContent:'center', flexWrap:'wrap'}}>
        <div style={{display:'flex', borderRadius:4, overflow:'hidden', border:`1px solid ${c.border}`}}>
          {['txns','days'].map(m => (
            <button key={m} style={{...segStyle(mode===m), borderRadius:0}}
              onClick={() => { setMode(m); setSpeedIdx(0); }}>
              {m==='txns' ? 'per-txn' : 'per-day'}
            </button>
          ))}
        </div>
        {SPEEDS[mode].map((s,i) => (
          <button key={s} style={pillStyle(speedIdx===i)} onClick={() => setSpeedIdx(i)}>
            {mode==='days' ? `${s}d` : `${s}×`}
          </button>
        ))}
      </div>

      {/* event log */}
      {milestones.length > 0 && (
        <div style={{flex:1, display:'flex', flexDirection:'column', gap:5, marginTop:4}}>
          <div style={{...mono, fontSize:8, color:c.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:2}}>events</div>
          {milestones.map((m,i) => {
            const passed = simDate && m.date <= simDate;
            return (
              <div key={`${m.date}-${i}`} style={{display:'flex', gap:7, alignItems:'flex-start', opacity: passed ? 1 : 0.25}}>
                <span style={{fontSize:11, flexShrink:0, lineHeight:1.4}}>{KIND_ICON[m.kind]||'·'}</span>
                <span style={{...mono, fontSize:10, lineHeight:1.4, color: passed ? c.text : c.muted, fontWeight: passed && m.kind !== 'month_rollover' ? 600 : 400}}>
                  {m.title}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* footer */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:8, borderTop:`1px solid ${c.border}`}}>
        <span style={{...mono, fontSize:8, color:c.dim}}>
          {simDate ? `sim · ${shortLabel(simDate)}` : 'sim idle'}
        </span>
        {simDate && (
          <button onClick={() => { setSimDate(null); setPlaying(false); }}
            style={{...mono, fontSize:8, padding:'2px 8px', borderRadius:6, border:'1px solid #c0392b', background:'transparent', color:'#c0392b', cursor:'pointer', letterSpacing:0.4}}>
            exit sim
          </button>
        )}
      </div>
    </div>
  );
}
